import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle, type LucideIcon } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAppSelector } from '../../store/hooks';
import BtnTaratic from './BtnTaratic';
import {
  NotificationContext,
  type NotificationApi,
  type NotificationInput,
  type NotificationVariant,
} from './notifications';

interface NotificationItem extends Required<Pick<NotificationInput, 'title' | 'variant' | 'duration'>> {
  id: string;
  message?: string;
}

interface VariantStyles {
  icon: LucideIcon;
  light: string;
  dark: string;
  iconLight: string;
  iconDark: string;
  progress: string;
  shadowLight: string;
  shadowDark: string;
}

const DEFAULT_DURATION = 5_000;
const EXIT_DURATION = 180;
let notificationSequence = 0;

const variantStyles: Record<NotificationVariant, VariantStyles> = {
  success: {
    icon: CheckCircle2,
    light: 'bg-green-100/95 text-green-950',
    dark: 'bg-green-950/95 text-green-50',
    iconLight: 'bg-white/60 text-green-800',
    iconDark: 'bg-black/15 text-green-200',
    progress: 'bg-green-500',
    shadowLight: 'shadow-glow-green-500-md',
    shadowDark: 'shadow-glow-green-900-md',
  },
  warning: {
    icon: AlertTriangle,
    light: 'bg-orange-100/95 text-orange-950',
    dark: 'bg-orange-950/95 text-orange-50',
    iconLight: 'bg-white/60 text-orange-800',
    iconDark: 'bg-black/15 text-orange-200',
    progress: 'bg-orange-500',
    shadowLight: 'shadow-glow-orange-500-md',
    shadowDark: 'shadow-glow-orange-900-md',
  },
  error: {
    icon: XCircle,
    light: 'bg-red-100/95 text-red-950',
    dark: 'bg-red-950/95 text-red-50',
    iconLight: 'bg-white/60 text-red-800',
    iconDark: 'bg-black/15 text-red-200',
    progress: 'bg-red-500',
    shadowLight: 'shadow-glow-red-500-md',
    shadowDark: 'shadow-glow-red-900-md',
  },
  info: {
    icon: Info,
    light: 'bg-blue-100/95 text-blue-950',
    dark: 'bg-blue-950/95 text-blue-50',
    iconLight: 'bg-white/60 text-blue-800',
    iconDark: 'bg-black/15 text-blue-200',
    progress: 'bg-blue-500',
    shadowLight: 'shadow-glow-blue-500-md',
    shadowDark: 'shadow-glow-blue-900-md',
  },
};

function makeNotificationId() {
  notificationSequence += 1;
  return `notification-${Date.now()}-${notificationSequence}`;
}

function NotificationToast({
  notification,
  isDark,
  onDismiss,
}: {
  notification: NotificationItem;
  isDark: boolean;
  onDismiss: NotificationApi['dismiss'];
}) {
  const [isPaused, setIsPaused] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const isLeavingRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const exitTimeoutRef = useRef<number | null>(null);
  const startedAtRef = useRef(Date.now());
  const remainingRef = useRef(notification.duration);
  const styles = variantStyles[notification.variant];
  const Icon = styles.icon;

  const clearDismissTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const requestDismiss = useCallback(() => {
    if (isLeavingRef.current) return;
    isLeavingRef.current = true;
    clearDismissTimer();
    setIsLeaving(true);
    exitTimeoutRef.current = window.setTimeout(() => onDismiss(notification.id), EXIT_DURATION);
  }, [clearDismissTimer, notification.id, onDismiss]);

  const startDismissTimer = useCallback(() => {
    clearDismissTimer();
    startedAtRef.current = Date.now();
    timeoutRef.current = window.setTimeout(requestDismiss, remainingRef.current);
  }, [clearDismissTimer, requestDismiss]);

  useEffect(() => {
    remainingRef.current = notification.duration;
    setIsPaused(false);
    startDismissTimer();
    return () => {
      clearDismissTimer();
      if (exitTimeoutRef.current !== null) window.clearTimeout(exitTimeoutRef.current);
    };
  }, [clearDismissTimer, notification.duration, startDismissTimer]);

  const pause = () => {
    if (isPaused || isLeavingRef.current) return;
    remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
    clearDismissTimer();
    setIsPaused(true);
  };

  const resume = () => {
    if (!isPaused || isLeavingRef.current) return;
    setIsPaused(false);
    startDismissTimer();
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) resume();
  };

  const handleMouseLeave = () => resume();

  return (
    <div
      role={notification.variant === 'error' ? 'alert' : 'status'}
      aria-atomic="true"
      data-leaving={isLeaving ? 'true' : 'false'}
      className={`notification-toast pointer-events-auto relative w-full overflow-hidden rounded-[14px] border-0 outline-none backdrop-blur-xl ${
        isDark ? styles.shadowDark : styles.shadowLight
      } ${
        isDark ? styles.dark : styles.light
      }`}
      onMouseEnter={pause}
      onMouseLeave={handleMouseLeave}
      onFocusCapture={pause}
      onBlurCapture={handleBlur}
    >
      <div className="flex items-start gap-2.5 p-3 sm:gap-3 sm:p-4">
        <div
          aria-hidden="true"
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9 sm:rounded-xl ${
            isDark ? styles.iconDark : styles.iconLight
          }`}
        >
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.25} />
        </div>

        <div className="min-w-0 flex-1 py-0.5">
          <p className="text-sm font-bold leading-5 tracking-[-0.01em] sm:text-[15px]">{notification.title}</p>
          {notification.message && (
            <p className="mt-0.5 break-words text-xs leading-[1.35rem] opacity-80 sm:mt-1 sm:text-sm">
              {notification.message}
            </p>
          )}
        </div>

        <BtnTaratic
          type="button"
          onClick={requestDismiss}
          color="ghost-dark"
          iconOnly
          data-ui="notification-close"
          className="!h-8 !w-8 shrink-0"
          aria-label="Cerrar notificación"
        >
          <FontAwesomeIcon icon={faTimes} />
        </BtnTaratic>
      </div>

      <div
        aria-hidden="true"
        className={`notification-progress absolute inset-x-0 bottom-0 h-1 origin-left ${styles.progress}`}
        style={{
          animationDuration: `${notification.duration}ms`,
          animationPlayState: isPaused ? 'paused' : 'running',
        }}
      />
    </div>
  );
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const theme = useAppSelector((state) => state.ui.theme);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 640px)').matches,
  );

  useEffect(() => {
    const media = window.matchMedia('(min-width: 640px)');
    const handleChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    setIsDesktop(media.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    setNotifications((current) => current.slice(0, isDesktop ? 3 : 2));
  }, [isDesktop]);

  const dismiss = useCallback((id: string) => {
    setNotifications((current) => current.filter((notification) => notification.id !== id));
  }, []);

  const notify = useCallback(
    ({ title, message, variant = 'info', duration = DEFAULT_DURATION }: NotificationInput) => {
      const id = makeNotificationId();
      const next: NotificationItem = {
        id,
        title,
        message,
        variant,
        duration: Math.max(1_000, duration),
      };

      setNotifications((current) => {
        const withoutDuplicate = current.filter(
          (item) =>
            item.title !== next.title || item.message !== next.message || item.variant !== next.variant,
        );
        return [next, ...withoutDuplicate].slice(0, isDesktop ? 3 : 2);
      });

      return id;
    },
    [isDesktop],
  );

  const api = useMemo<NotificationApi>(
    () => ({
      notify,
      success: (title, message) => notify({ title, message, variant: 'success' }),
      warning: (title, message) => notify({ title, message, variant: 'warning' }),
      error: (title, message) => notify({ title, message, variant: 'error' }),
      info: (title, message) => notify({ title, message, variant: 'info' }),
      dismiss,
    }),
    [dismiss, notify],
  );

  return (
    <NotificationContext.Provider value={api}>
      {children}
      <div
        data-theme={theme}
        className="pointer-events-none fixed inset-x-0 top-[max(0.5rem,env(safe-area-inset-top))] z-[20000] flex flex-col items-center gap-2 px-2 sm:top-[max(1rem,env(safe-area-inset-top))] sm:gap-3 sm:px-4"
        aria-label="Notificaciones"
      >
        {notifications.map((notification) => (
          <div key={notification.id} className="w-full max-w-[340px] sm:max-w-[400px]">
            <NotificationToast
              notification={notification}
              isDark={theme === 'dark'}
              onDismiss={dismiss}
            />
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}
